import { LatLng, LatLngBoundsExpression, LeafletMouseEvent } from 'leaflet'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Marker, Polygon, Polyline, useMap, useMapEvent } from 'react-leaflet'
import * as turf from '@turf/turf'
import L from 'leaflet'
import { destroyAlert, errorAlert } from '../../../../../utils/alert'
import { ContainerPoint } from '../../styles'
import './style.css'
import { Render } from '../../../../../Components/CustomUi/render'
import ModalConfirmar from '../modalConfirmar'
import { encontrarIndexDoPoligono, gerarGrid, rotatePoint } from '../../meta'

type Coordenada = {
  lat: number
  lng: number
}

export type GridMalha = {
  id?: number
  center: LatLngTuple
  oculto: boolean
  idPolygon: number
  posicao?: number
  polygons: turf.Feature<turf.Polygon>
}

export type GridPolygon = {
  id: number
  polygon: turf.Feature<turf.Polygon>
}

interface PropsGrids {
  polygon: turf.Feature
  novoPonto: boolean
  moverPonto: boolean
  removerPonto: boolean
  selecionarPonto: boolean
  rotation?: number
  isSliding: boolean
  gridLevel: number
  moverVertical: number
  moverHorizontal: number
  tab_selecionado: string
  abaAtual: number
  grid: GridMalha[]
  setGrid: React.Dispatch<React.SetStateAction<GridMalha[]>>
  gridOriginal: GridMalha[]
  setGridOriginal: React.Dispatch<React.SetStateAction<GridMalha[]>>
  gridOriginalRotacao: GridMalha[]
  setGridOriginalRotacao: React.Dispatch<React.SetStateAction<GridMalha[]>>
  setTamanhoGridOriginal: React.Dispatch<React.SetStateAction<number>>
  abrirModal: boolean
  setAbrirModal: React.Dispatch<React.SetStateAction<boolean>>
  confirmado: boolean
  setConfirmado: React.Dispatch<React.SetStateAction<boolean>>
  pontosSelecionados: any[]
  setPontosSelecionados: React.Dispatch<React.SetStateAction<any[]>>
  mousePosition: [number, number] | null
  setMousePosition: React.Dispatch<React.SetStateAction<[number, number] | null>>
  ancora: LatLng | null
}

export const AjustarBounds = ({ coordenadas }: { coordenadas: Coordenada[] }) => {
  const map = useMap()

  useEffect(() => {
    if (coordenadas.length === 0) return

    const bounds: LatLngBoundsExpression = coordenadas.map(({ lat, lng }) => [lat, lng])

    map.fitBounds(bounds)
  }, [coordenadas, map])

  return null
}

type LatLngTuple = [number, number]

/* ---------- AJUDA ---------- */
// Se vier MultiPolygon, pega o primeiro polígono
const getFirstPolygon = (feat: turf.Feature) =>
  feat.geometry.type === 'Polygon' ? feat : turf.flatten(feat).features[0]

/* ---------- COMPONENTE ---------- */
export const MalhaGrid: React.FC<PropsGrids> = ({
  polygon: raw,
  novoPonto,
  moverPonto,
  removerPonto,
  selecionarPonto,
  rotation = 0,
  isSliding,
  moverVertical = 0,
  moverHorizontal = 0,
  tab_selecionado,
  grid,
  setGrid,
  gridOriginal,
  setGridOriginal,
  gridOriginalRotacao,
  setGridOriginalRotacao,
  abrirModal,
  setAbrirModal,
  setConfirmado,
  pontosSelecionados,
  setPontosSelecionados,
  mousePosition,
  setMousePosition,
  ancora,
}) => {
  const popupRefs = useRef({})
  const polygon = getFirstPolygon(raw)
  const [celulasKm, setCelulasKm] = useState(0)
  const [malhaNova, setMalhaNova] = useState([])
  // const map = useMap()

  // const unkinkedFeatureCollection = turf.unkinkPolygon(polygon.geometry)

  // const cleanedFeatures = unkinkedFeatureCollection.features.map((f) =>
  //   turf.buffer(f.geometry, 0, { units: 'meters' })
  // )

  // const finalGeoJSON = {
  //   type: 'FeatureCollection',
  //   features: cleanedFeatures.map((geom) => ({ type: 'Feature', geometry: geom, properties: {} })),
  // }


  useMapEvent('click', (e: LeafletMouseEvent) => {
    const { lat, lng } = e.latlng
    const point = turf.point([lng, lat])
    const estaDentro = turf.booleanPointInPolygon(point, polygon)

    const indexDoPoligono = encontrarIndexDoPoligono(point, malhaNova)

    const pontoExistePolygon = grid.filter(
      (item) => item.idPolygon === malhaNova[indexDoPoligono]?.id
    )

    if (novoPonto && estaDentro && tab_selecionado === 'Pontos' && novoPonto) {
      if (pontoExistePolygon[0]?.oculto === false) {
        errorAlert('Não pode adicionar um novo ponto, ponto já existente')
        return
      } else {
        setGrid((prev) =>
          prev.map((item) =>
            item.idPolygon === pontoExistePolygon[0]?.idPolygon
              ? { ...item, center: [lat, lng], oculto: false }
              : item
          )
        )
        setGridOriginalRotacao((prev) =>
          prev.map((item) =>
            item.idPolygon === pontoExistePolygon[0]?.idPolygon
              ? { ...item, center: [lat, lng], oculto: false }
              : item
          )
        )
      }
    }
  })

  function MouseTracker() {
    useMapEvent('mousemove', (e) => {
      if (pontosSelecionados.length === 1) {
        setMousePosition([e.latlng.lat, e.latlng.lng])
      }
    })
    return null
  }

  function remocaoDosPontos(id: number) {
    setGrid((prev) => prev.map((item) => (item?.id === id ? { ...item, oculto: true } : item)))
    setGridOriginalRotacao((prev) =>
      prev.map((item) => (item?.id === id ? { ...item, oculto: true } : item))
    )

    const popup = popupRefs.current[id]
    if (popup) {
      popup._source.closePopup() // _source é o Marker
    }
  }

  function movimentarPonto(e, id, idPolygon) {
    const newLatLng = e.target.getLatLng()
    const point = turf.point([newLatLng?.lng, newLatLng?.lat])
    const indexDoPoligono = encontrarIndexDoPoligono(point, malhaNova)

    setGrid((prev) =>
      prev.map((cell) => {
        if (cell.id !== id) return cell

        const turfPoint = turf.point([newLatLng.lng, newLatLng.lat])

        const isInside = turf.booleanPointInPolygon(turfPoint, polygon)

        if (!isInside) {
          errorAlert('Ponto movido para fora do talhão!')
          return cell // Não atualiza o centro se estiver fora
        }

        if (malhaNova[indexDoPoligono]?.id !== idPolygon) {
          errorAlert('Ponto movido para fora da area da malha')
          return cell
        }

        return {
          ...cell,

          center: [newLatLng.lat, newLatLng.lng] as LatLngTuple,
        }
      })
    )

    setGridOriginal((prev) =>
      prev.map((cell) => {
        if (cell.id !== id) return cell

        return {
          ...cell,

          center: [newLatLng.lat, newLatLng.lng] as LatLngTuple,
        }
      })
    )

    setGridOriginalRotacao((prev) =>
      prev.map((cell) => {
        if (cell.id !== id) return cell

        return {
          ...cell,

          center: [newLatLng.lat, newLatLng.lng] as LatLngTuple,
        }
      })
    )
  }

  function selecionar(ponto) {
    if (pontosSelecionados.length >= 2) {
      return
    }

    setPontosSelecionados((prev) => [...prev, ponto])
  }

  function mudarCursor(e) {
    let el = e.target.getElement()
    if (el) el.style.cursor = novoPonto ? 'crosshair' : ''
  }

  function distancia([x1, y1]: [number, number], [x2, y2]: [number, number]): number {
    return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2)
  }

  function ordenarZigZagPorDirecao(
    grid,
    ponto1: [number, number],
    ponto2: [number, number],
    opcoes?: { forcarInicioEmPonto1?: boolean }
  ) {
   const [x1, y1] = ponto1
  const [x2, y2] = ponto2

  const dx = x2 - x1
  const dy = y2 - y1
  const mag = Math.sqrt(dx * dx + dy * dy)
  const dirX = dx / mag
  const dirY = dy / mag
  const perpX = -dirY
  const perpY = dirX

  const tolerancia = 0.0003

  // Projeta cada ponto em um sistema baseado na direção
  const projetados = grid.map((ponto) => {
    const [px, py] = ponto.center
    const relX = px - x1
    const relY = py - y1

    const projPrincipal = relX * dirX + relY * dirY
    const projPerpendicular = relX * perpX + relY * perpY

    return {
      ...ponto,
      _proj: projPrincipal,
      _offset: projPerpendicular,
    }
  })

  // Agrupa os pontos por faixa (com base no offset perpendicular)
  const faixas: (typeof projetados)[][] = []
  for (const ponto of projetados) {
    let faixa = faixas.find((g) =>
      g.some((p) => Math.abs(p._offset - ponto._offset) <= tolerancia)
    )
    if (faixa) faixa.push(ponto)
    else faixas.push([ponto])
  }

  // Ordena as faixas pela média do offset
  faixas.sort((a, b) => {
    const mediaA = a.reduce((s, p) => s + p._offset, 0) / a.length
    const mediaB = b.reduce((s, p) => s + p._offset, 0) / b.length
    return mediaA - mediaB
  })

  // Encontrar a faixa do ponto1
  const indexFaixaPonto1 = faixas.findIndex((faixa) =>
    faixa.some(
      (p) =>
        Math.abs(p.center[0] - x1) <= tolerancia &&
        Math.abs(p.center[1] - y1) <= tolerancia
    )
  )

  if (indexFaixaPonto1 === -1) {
    console.log("Ponto1 não encontrado.")
    return
  }

  // Reorganiza as faixas começando pela faixa do ponto1
  const faixaCentral = faixas[indexFaixaPonto1]
  const antes = faixas.slice(0, indexFaixaPonto1).reverse()
  const depois = faixas.slice(indexFaixaPonto1 + 1)
  const novaOrdemFaixas = [faixaCentral, ...depois, ...antes]

  const resultado: GridMalha[] = []
  let contador = 1

  novaOrdemFaixas.forEach((faixa, i) => {
    if (i === 0) {
      // Começa a partir do ponto1 na primeira faixa
      const idx = faixa.findIndex(
        (p) =>
          Math.abs(p.center[0] - x1) <= tolerancia &&
          Math.abs(p.center[1] - y1) <= tolerancia
      )
      const ordenada = faixa
        .slice(idx)
        .concat(faixa.slice(0, idx))
        .sort((a, b) => a._proj - b._proj)
      ordenada.forEach((p) => {
        p.posicao = contador++
        resultado.push(p)
      })
    } else {
      faixa.sort((a, b) =>
        i % 2 === 0 ? a._proj - b._proj : b._proj - a._proj
      )
      faixa.forEach((p) => {
        p.posicao = contador++
        resultado.push(p)
      })
    }
  })

  setGrid(resultado)
}
  function verificarPosicao(posicao) {
    if (posicao === undefined) {
      return removerPonto ? 'X' : ''
    }

    return posicao
  }

  function opcaoSelecionar(lat?: number, lng?: number) {
    if (
      pontosSelecionados[0]?.[0] === lat &&
      pontosSelecionados[0]?.[1] === lng &&
      pontosSelecionados[0]?.[0] !== undefined &&
      pontosSelecionados[0]?.[1] !== undefined
    ) {
      return 'ponto1'
    }

    if (moverPonto) {
      return 'mover'
    }

    if (removerPonto) {
      return 'remover'
    }
  }

  useEffect(() => {
    const offsetLat = moverVertical / 111320
    const offsetLng = moverHorizontal / (111320 * Math.cos((ancora[1] * Math.PI) / 180))

    let cells: GridMalha[] = []

    cells = gridOriginal.map((cell, index) => {
      const centerCoords = turf.centerOfMass(cell.polygons).geometry.coordinates
      const [lng, lat] = centerCoords

      const movedLat = lat + offsetLat
      const movedLng = lng + offsetLng

      const movedCoords = cell.polygons.geometry.coordinates[0].map(([lng, lat]) => {
        return [lng + offsetLng, lat + offsetLat]
      })

      const movedPolygon = turf.polygon([movedCoords])

      return {
        id: cell.id,
        center: [movedLat, movedLng],
        idPolygon: cell?.idPolygon,
        oculto: cell?.oculto,

        polygons: movedPolygon,
        // aqui você já está pegando o centro correto, não precisa somar offset de novo
      }
    })

    if (rotation !== 0) {
      // malhaPolygonsMover = malhaPolygonsMover.map((item) => {
      //   const rotatedCoords = item?.polygon.geometry.coordinates[0].map(([lng, lat]) => {
      //     const [rotLat, rotLng] = rotatePoint([lat, lng], [ancora[1], ancora[0]], rotation)
      //     return [rotLng, rotLat]
      //   })
      //   const polygon = turf.polygon([rotatedCoords])

      //   return {
      //     id: item?.id,
      //     polygon,
      //   }
      // })
      cells = cells.map((item) => {
        const rotatedCenter = rotatePoint(item?.center, [ancora[1], ancora[0]], rotation)
        const rotatedCoords = item?.polygons.geometry.coordinates[0].map(([lng, lat]) => {
          const [rotLat, rotLng] = rotatePoint([lat, lng], [ancora[1], ancora[0]], rotation)
          return [rotLng, rotLat]
        })
        const rotatedPolygon = turf.polygon([rotatedCoords])
        return {
          ...item,
          center: rotatedCenter,
          polygons: rotatedPolygon,
        }
      })
    }

    cells = cells.map((item) => {
      const ponto = turf.point([item.center[1], item.center[0]])
      const dentro = turf.booleanPointInPolygon(ponto, polygon)

      return {
        ...item,

        oculto: dentro ? false : true,
      }
    })

    // const fixedRef = turf.buffer(polygon, 0, { units: 'meters' })

    // const transformada = cells.map((cell) => {
    //   const coords = cell.polygons.geometry.coordinates[0]

    //   // Aplica deslocamento e rotação
    //   const moved = coords.map(([lng, lat]) => {
    //     const latOffset = lat + moverVertical / 111320
    //     const lngOffset = lng + moverHorizontal / (111320 * Math.cos((lat * Math.PI) / 180))
    //     const [rotLat, rotLng] = rotatePoint(
    //       [latOffset, lngOffset],
    //       [ancora[1], ancora[0]],
    //       rotation
    //     )
    //     return [rotLng, rotLat]
    //   })

    //   const polygonTransformado = turf.polygon([moved])

    //   const intersected = turf.intersect(polygonTransformado, fixedRef)

    //   return {
    //     polygon: polygonTransformado,
    //     coordsLatLng: (intersected || polygonTransformado).geometry.coordinates[0].map(
    //       ([lng, lat]) => [lat, lng]
    //     ),
    //     visivel: !intersected || intersected.geometry.type === 'Polygon',
    //   }
    // })

    if (rotation === 0) {
      setGrid(gridOriginal)
      // setMalhaPolygon(malhaPolygonOriginal) // reseta quando zera a rotação
    }
    // console.log(transformada)
    // setMalhaNova(transformada)

    setGrid(cells?.length === 0 ? gridOriginal : cells)
    setGridOriginalRotacao(cells?.length === 0 ? gridOriginal : cells)
  }, [rotation, moverHorizontal, moverVertical])

  useEffect(() => {
    if (!grid) return
    const handler = setTimeout(() => {
      const fixedRef = turf.buffer(polygon, 0, { units: 'meters' })

      const novaMalha = (tab_selecionado === 'Ordenacao' ? gridOriginalRotacao : grid)
        .map((cell, i) => {
          const intersected = turf.intersect(cell.polygons, fixedRef)

          // ❗️ Filtro essencial: só adiciona se houver interseção válida
          if (!intersected || !['Polygon', 'MultiPolygon'].includes(intersected.geometry.type)) {
            return null
          }

          const coordsLatLng = intersected.geometry.coordinates[0].map(([lng, lat]) => [lat, lng])

          return {
            id: cell?.idPolygon,
            polygon: cell?.polygons,
            coordsLatLng,
          }
        })
        .filter(Boolean) // remove os nulos

      setMalhaNova(novaMalha)
    }, 800) // executa fora da call stack principal

    // Limpa se o efeito for reexecutado rapidamente
    return () => clearTimeout(handler)
  }, [grid, moverHorizontal, moverVertical, rotation])

  useEffect(() => {
    if (pontosSelecionados.length === 2) {
      const pontosFiltradosTalhao = grid.filter(item => item?.oculto === false)
      ordenarZigZagPorDirecao(pontosFiltradosTalhao, pontosSelecionados[0], pontosSelecionados[1], {
        forcarInicioEmPonto1: true,
      })
    }
  }, [pontosSelecionados])

  return (
    <ContainerPoint>
      <Render condition={pontosSelecionados.length === 1}>
        <MouseTracker />
      </Render>

      {pontosSelecionados.length === 1 && mousePosition && (
        <Polyline
          positions={[pontosSelecionados[0], mousePosition]}
          pathOptions={{ color: 'red', dashArray: '4, 4', weight: 2 }}
        />
      )}
      {grid
        .filter((item) => item?.oculto === false)
        .map(({ id, idPolygon, center, polygons, posicao }, i) => {
          if (!center) return null
          const [lat, lng] = center

          const pointMarker = (
            <Marker
              key={`pt-${i}`}
              position={[lat, lng]}
              icon={L.divIcon({
                className: 'custom-marker',
                html: `<div data-open="${opcaoSelecionar(lat, lng)}">${verificarPosicao(
                  posicao
                )}</div>`,
              })}
              draggable={moverPonto}
              eventHandlers={{
                dragend: (e) => {
                  if (!moverPonto) {
                    return
                  }
                  movimentarPonto(e, id, idPolygon)
                },
                contextmenu: () => {
                  if (removerPonto) {
                    remocaoDosPontos(id)
                  }
                },
                click: () => {
                  if (selecionarPonto) {
                    selecionar(center)
                  }
                },
              }}
            />
          )

          if (isSliding === false && tab_selecionado === 'Mapa') {
            const fixedItem = turf.buffer(polygons, 0, { units: 'meters' })
            const fixedRef = turf.buffer(polygon, 0, { units: 'meters' })
            const intersected = turf.intersect(fixedItem, fixedRef)

            if (!polygons || polygons?.geometry.type !== 'Polygon') {
              return
            }

            if (!intersected || !['Polygon', 'MultiPolygon'].includes(intersected.geometry.type))
              return

            const coords = intersected.geometry.coordinates[0].map(([lng, lat]) => [lat, lng])
            return (
              <Fragment key={i}>
                <Polygon
                  positions={coords}
                  pathOptions={{ color: 'black', weight: 1, fillOpacity: 0 }}
                />
                {pointMarker}
              </Fragment>
            )
          }

          return pointMarker
        })}

      {(tab_selecionado === 'Pontos' || tab_selecionado === 'Ordenacao') &&
        malhaNova.map((cell) => {
          return (
            <Polygon
              key={cell.id}
              positions={cell.coordsLatLng}
              pathOptions={{
                color: 'black',
                weight: 1,
                fillOpacity: 0,
              }}
            />
          )
        })}
      <ModalConfirmar
        abrir={abrirModal}
        abaAtual={tab_selecionado}
        setAbrir={setAbrirModal}
        setConfirmado={setConfirmado}
      />
    </ContainerPoint>
  )
}
